import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import type { EmergencyContact } from '@/types/employee';
import type { CreateEmergencyContactInput, UpdateEmergencyContactInput } from '@/lib/employees';

interface EmergencyContactModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employeeId: string;
  contact?: EmergencyContact | null;
  onSave: (data: CreateEmergencyContactInput | UpdateEmergencyContactInput) => Promise<void>;
}

const RELATIONSHIP_OPTIONS = [
  { value: 'spouse', label: 'Spouse' },
  { value: 'parent', label: 'Parent' },
  { value: 'sibling', label: 'Sibling' },
  { value: 'friend', label: 'Friend' },
  { value: 'other', label: 'Other' },
] as const;

const MESSENGER_FIELDS = [
  { key: 'telegram', label: 'Telegram', placeholder: '@username' },
  { key: 'whatsapp', label: 'WhatsApp', placeholder: '+380 50 123 4567' },
  { key: 'viber', label: 'Viber', placeholder: '+380 50 123 4567' },
  { key: 'signal', label: 'Signal', placeholder: '+380 50 123 4567' },
  { key: 'discord', label: 'Discord', placeholder: 'username' },
  { key: 'linkedin', label: 'LinkedIn', placeholder: 'https://linkedin.com/in/...' },
] as const;

type MessengerKey = (typeof MESSENGER_FIELDS)[number]['key'];

type FormState = {
  name: string;
  relationship: CreateEmergencyContactInput['relationship'];
  phone: string;
  email: string;
  isPrimary: boolean;
} & Record<MessengerKey, string>;

const emptyForm: FormState = {
  name: '',
  relationship: 'spouse',
  phone: '',
  email: '',
  telegram: '',
  whatsapp: '',
  viber: '',
  signal: '',
  discord: '',
  linkedin: '',
  isPrimary: false,
};

export function EmergencyContactModal({
  open,
  onOpenChange,
  employeeId: _employeeId,
  contact,
  onSave,
}: EmergencyContactModalProps) {
  const isEdit = !!contact;
  const [formData, setFormData] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState | 'form', string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (contact) {
      setFormData({
        name: contact.name,
        relationship: contact.relationship as CreateEmergencyContactInput['relationship'],
        phone: contact.phone ?? '',
        email: contact.email ?? '',
        telegram: contact.telegram ?? '',
        whatsapp: contact.whatsapp ?? '',
        viber: contact.viber ?? '',
        signal: contact.signal ?? '',
        discord: contact.discord ?? '',
        linkedin: contact.linkedin ?? '',
        isPrimary: contact.isPrimary,
      });
    } else {
      setFormData(emptyForm);
    }
    setErrors({});
  }, [open, contact]);

  const contactMethodKeys: (keyof FormState)[] = [
    'phone',
    'email',
    ...MESSENGER_FIELDS.map((f) => f.key),
  ];

  const hasAnyContactMethod = () =>
    contactMethodKeys.some((k) => {
      const v = formData[k];
      return typeof v === 'string' && v.trim().length > 0;
    });

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof FormState | 'form', string>> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.length > 200) {
      newErrors.name = 'Name must be less than 200 characters';
    }

    if (formData.phone && formData.phone.length > 50) {
      newErrors.phone = 'Phone must be less than 50 characters';
    }

    if (formData.email && formData.email.length > 0) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = 'Invalid email format';
      } else if (formData.email.length > 255) {
        newErrors.email = 'Email must be less than 255 characters';
      }
    }

    for (const { key, label } of MESSENGER_FIELDS) {
      if (formData[key] && formData[key].length > 255) {
        newErrors[key] = `${label} must be less than 255 characters`;
      }
    }

    if (!hasAnyContactMethod()) {
      newErrors.form = 'At least one contact method is required (phone, email, or any messenger).';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const trim = (s: string) => s.trim();
      const dataToSave: CreateEmergencyContactInput | UpdateEmergencyContactInput = {
        name: trim(formData.name),
        relationship: formData.relationship,
        ...(trim(formData.phone) && { phone: trim(formData.phone) }),
        ...(trim(formData.email) && { email: trim(formData.email) }),
        ...(trim(formData.telegram) && { telegram: trim(formData.telegram) }),
        ...(trim(formData.whatsapp) && { whatsapp: trim(formData.whatsapp) }),
        ...(trim(formData.viber) && { viber: trim(formData.viber) }),
        ...(trim(formData.signal) && { signal: trim(formData.signal) }),
        ...(trim(formData.discord) && { discord: trim(formData.discord) }),
        ...(trim(formData.linkedin) && { linkedin: trim(formData.linkedin) }),
        ...(formData.isPrimary && { isPrimary: formData.isPrimary }),
      };

      await onSave(dataToSave);
      onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Emergency Contact' : 'Add Emergency Contact'}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Update the emergency contact information.'
              : 'Add a new emergency contact for this employee.'}{' '}
            At least one contact method (phone, email, or messenger) is required.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Full name"
                disabled={isSubmitting}
                className={errors.name ? 'border-destructive' : ''}
              />
              {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="relationship">
                Relationship <span className="text-destructive">*</span>
              </Label>
              <Select
                value={formData.relationship}
                onValueChange={(value) =>
                  setFormData({
                    ...formData,
                    relationship: value as CreateEmergencyContactInput['relationship'],
                  })
                }
                disabled={isSubmitting}
              >
                <SelectTrigger id="relationship">
                  <SelectValue placeholder="Select relationship" />
                </SelectTrigger>
                <SelectContent>
                  {RELATIONSHIP_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+380 50 123 4567"
                  disabled={isSubmitting}
                  className={errors.phone ? 'border-destructive' : ''}
                />
                {errors.phone && <p className="text-sm text-destructive">{errors.phone}</p>}
              </div>
              <div className="grid gap-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@example.com"
                  disabled={isSubmitting}
                  className={errors.email ? 'border-destructive' : ''}
                />
                {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {MESSENGER_FIELDS.map(({ key, label, placeholder }) => (
                <div key={key} className="grid gap-2">
                  <Label htmlFor={key}>{label}</Label>
                  <Input
                    id={key}
                    value={formData[key]}
                    onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                    placeholder={placeholder}
                    disabled={isSubmitting}
                    className={errors[key] ? 'border-destructive' : ''}
                  />
                  {errors[key] && <p className="text-sm text-destructive">{errors[key]}</p>}
                </div>
              ))}
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="isPrimary"
                checked={formData.isPrimary}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, isPrimary: checked === true })
                }
                disabled={isSubmitting}
              />
              <Label
                htmlFor="isPrimary"
                className="text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
              >
                Set as primary contact
              </Label>
            </div>

            {errors.form && (
              <p className="text-sm text-destructive" role="alert">
                {errors.form}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : isEdit ? 'Update' : 'Add Contact'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
